import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { AlertCircle, Phone, Calculator, Clock, CheckCircle, ChevronUp, ChevronDown } from 'lucide-react';

import { getDynamicPhoneNumber } from '../../services/constants';
import BailEstimator from './components/BailEstimator';
import CountyProcessGuide from './components/CountyProcessGuide';
import DownloadableChecklists from './components/DownloadableChecklists';
import CredentialsShowcase from './components/CredentialsShowcase';

const BailGuideWithFAQ = () => {
  const [fallbackPhone, setFallbackPhone] = useState('+19162263934');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [language, setLanguage] = useState('en');

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19162263934');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  // FAQ Data with English/Spanish support
  const faqData = {
    en: [
      {
        category: "Process & Timeline",
        questions: [
          {
            question: "How long does the bail process actually take?",
            answer: "The complete process typically takes 2-4 hours from payment to release. This includes: paperwork processing (30 minutes), jail notification (15 minutes), and facility processing time (1-3 hours depending on the jail). Our 10-minute promise refers to how quickly we can start the process once you call us."
          },
          {
            question: "What happens if my loved one is released before I can arrange bail?",
            answer: "If they're released on their own recognizance (OR) or other means before we post bail, you pay nothing. Our 'no obligation' policy means you only pay if we successfully provide our services."
          },
          {
            question: "Can you really get someone out of jail at 3 AM?",
            answer: "Yes, we operate 24/7/365. Jails process releases around the clock, though some facilities may have limited overnight staff which could extend processing time to 4-6 hours during late night hours."
          }
        ]
      },
      {
        category: "Payment & Costs",
        questions: [
          {
            question: "What does 'flexible payment plans' actually mean?",
            answer: "We offer payment plans starting at $50 down for qualified applicants. Plans can be structured over 3-12 months depending on the bail amount and your financial situation. We accept credit cards, debit cards, and electronic transfers."
          },
          {
            question: "Are there really no hidden fees or junk charges?",
            answer: "Correct. Our fee is 10% of the bail amount as required by California law, plus actual costs for any travel outside our immediate service area. We disclose all costs upfront before you sign anything."
          },
          {
            question: "What collateral do you require?",
            answer: "Collateral requirements depend on the bail amount and flight risk assessment. For amounts under $25,000, we often require minimal collateral. For larger amounts, we may require property liens, vehicle titles, or co-signers."
          }
        ]
      },
      {
        category: "Legal & Requirements",
        questions: [
          {
            question: "What information do you need to get started?",
            answer: "Minimum required: defendant's full name and booking facility. Helpful additional info: booking number, charges, and bail amount (we can look these up if you don't have them). For faster processing, have a co-signer's ID and contact information ready."
          },
          {
            question: "What are my responsibilities as a co-signer?",
            answer: "As co-signer, you're responsible for ensuring the defendant appears for all court dates and follows all bail conditions. If they fail to appear, you're liable for the full bail amount. We provide court date reminders and check-ins to help prevent issues."
          },
          {
            question: "What happens if the defendant misses court?",
            answer: "If a defendant misses court, the judge may issue a warrant and forfeit the bail. We have a grace period (usually 30-180 days depending on the court) to locate and return the defendant to custody. Our recovery specialists work to resolve the situation before full forfeiture."
          }
        ]
      }
    ],
    es: [
      {
        category: "Proceso y Tiempo",
        questions: [
          {
            question: "¿Cuánto tiempo toma realmente el proceso de fianza?",
            answer: "El proceso completo típicamente toma 2-4 horas desde el pago hasta la liberación. Esto incluye: procesamiento de documentos (30 minutos), notificación a la cárcel (15 minutos), y tiempo de procesamiento de la instalación (1-3 horas dependiendo de la cárcel). Nuestra promesa de 10 minutos se refiere a qué tan rápido podemos comenzar el proceso una vez que nos llame."
          },
          {
            question: "¿Qué pasa si mi ser querido es liberado antes de que pueda arreglar la fianza?",
            answer: "Si son liberados bajo su propio reconocimiento (OR) u otros medios antes de que publiquemos la fianza, no paga nada. Nuestra póliza de 'sin obligación' significa que solo paga si proveemos exitosamente nuestros servicios."
          },
          {
            question: "¿Realmente pueden sacar a alguien de la cárcel a las 3 AM?",
            answer: "Sí, operamos 24/7/365. Las cárceles procesan liberaciones las 24 horas, aunque algunas instalaciones pueden tener personal limitado durante la noche lo cual podría extender el tiempo de procesamiento a 4-6 horas durante las horas de madrugada."
          }
        ]
      }
    ]
  };

  const currentFAQ = faqData?.[language] || faqData?.en;

  // 5-Step Process Data
  const stepsData = {
    en: [
      {
        step: 1,
        title: "Arrest & Booking",
        duration: "1-4 hours",
        description: "Person is arrested and taken to jail for booking process including fingerprinting, photographing, and paperwork.",
        requirements: ["Valid ID processed", "Charges entered into system", "Bail amount set by schedule or judge"],
        icon: "👮",
        timeframe: "Cannot post bail until booking is complete"
      },
      {
        step: 2,
        title: "Bail Setting",
        duration: "Immediate to 72 hours",
        description: "Bail amount is determined by preset schedule for common charges or set by judge during arraignment for serious offenses.",
        requirements: ["Booking must be complete", "Charges officially filed", "Judge availability (if required)"],
        icon: "⚖️",
        timeframe: "Most misdemeanors have preset bail amounts"
      },
      {
        step: 3,
        title: "Contacting Bail Agent",
        duration: "Immediate",
        description: "Call us immediately after learning of the arrest. We begin the process while they're still being booked.",
        requirements: ["Defendant\'s full name", "Arrest location/jail", "Contact information for co-signer"],
        icon: "📞",
        timeframe: "We answer in under 10 seconds, 24/7"
      },
      {
        step: 4,
        title: "Documentation & Payment",
        duration: "15-30 minutes",
        description: "Complete paperwork via secure electronic signature and arrange payment through our flexible options.",
        requirements: ["Signed bail agreement", "Payment arrangement", "Co-signer documentation"],
        icon: "📋",
        timeframe: "Done entirely by phone and secure link"
      },
      {
        step: 5,
        title: "Release Process",
        duration: "1-3 hours",
        description: "We post bail at the facility and they begin processing the release. Defendant is released with court date information.",
        requirements: ["Bail posted with facility", "Release paperwork processed", "Court date scheduled"],
        icon: "🏠",
        timeframe: "Facility processing time varies by location"
      }
    ],
    es: [
      {
        step: 1,
        title: "Arresto y Registro",
        duration: "1-4 horas",
        description: "La persona es arrestada y llevada a la cárcel para el proceso de registro incluyendo huellas dactilares, fotografías y papeleo.",
        requirements: ["ID válida procesada", "Cargos ingresados en el sistema", "Monto de fianza establecido por horario o juez"],
        icon: "👮",
        timeframe: "No se puede publicar fianza hasta que se complete el registro"
      }
    ]
  };

  const currentSteps = stepsData?.[language] || stepsData?.en;

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  return (
    <>
      <Helmet>
        <title>5-Step Bail Process Guide & FAQ | How Bail Bonds Work | Sailors Bail Bonds</title>
        <meta name="description" content="Complete guide to the bail process in 5 simple steps. Get answers to common bail bond questions, county-specific procedures, and downloadable checklists. Expert guidance from licensed agents." />
        <meta name="keywords" content="bail process guide, how bail works, bail bond steps, FAQ bail bonds, Sacramento bail process, jail release guide, bail timeline, bail requirements" />
        
        {/* Structured data for FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": currentFAQ?.flatMap(category => 
              category?.questions?.map(faq => ({
                "@type": "Question",
                "name": faq?.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq?.answer
                }
              }))
            )
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Crisis Support Banner */}
        <section className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 px-4 shadow-lg sticky top-0 z-50" role="banner">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center text-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 animate-pulse" aria-hidden="true" />
                <span className="font-bold">24/7 Crisis Support Active</span>
              </div>
              <div className="text-sm opacity-90">Need bail help right now? We're standing by.</div>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-6 py-2 rounded-full font-bold hover:bg-red-50 transition-colors text-sm"
                aria-label={`Call emergency bail service ${formatPhoneDisplay(fallbackPhone)}`}
              >
                Call Now: {formatPhoneDisplay(fallbackPhone)}
              </a>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-20" role="main">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <h1 className="text-4xl lg:text-6xl font-black mb-6 leading-tight">
                How Bail Works: 
                <span className="block bg-gradient-to-r from-yellow-300 to-amber-300 bg-clip-text text-transparent">
                  5-Step Visual Guide
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
                Understand the bail process from arrest to release. Get answers to your questions and download helpful checklists for each step.
              </p>

              {/* Language Toggle */}
              <div className="flex justify-center mb-8">
                <button
                  onClick={toggleLanguage}
                  className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold transition-all flex items-center"
                >
                  {language === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
                  <span className="mx-2">|</span>
                  {language === 'en' ? 'Switch to Español' : 'Switch to English'}
                </button>
              </div>

              {/* Quick Action Buttons */}
              <div className="flex flex-col lg:flex-row gap-4 justify-center">
                <a 
                  href={`tel:${fallbackPhone}`}
                  className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-8 py-4 rounded-2xl font-bold text-lg hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center"
                >
                  <Phone className="w-6 h-6 mr-3" aria-hidden="true" />
                  {language === 'es' ? 'Llamar Ahora' : 'Call Now - 24/7'}
                </a>
                
                <a 
                  href="#bail-calculator"
                  className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center"
                >
                  <Calculator className="w-6 h-6 mr-3" aria-hidden="true" />
                  {language === 'es' ? 'Calculadora de Fianza' : 'Bail Calculator'}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 5-Step Process Visual Guide */}
        <section className="py-20 bg-gray-50" role="region" aria-labelledby="process-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="process-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                {language === 'es' ? 'Proceso de Fianza en 5 Pasos' : 'The 5-Step Bail Process'}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                {language === 'es' ?'Entienda exactamente qué pasa desde el arresto hasta la liberación con estimaciones de tiempo realistas.' :'Understand exactly what happens from arrest to release with realistic time estimates.'
                }
              </p>
            </div>

            {/* Process Steps */}
            <div className="space-y-8">
              {currentSteps?.map((step, index) => (
                <div key={step?.step} className="relative">
                  {/* Connection Line */}
                  {index < currentSteps?.length - 1 && (
                    <div className="absolute left-8 top-20 w-1 h-16 bg-blue-300 z-10 hidden lg:block"></div>
                  )}
                  
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                    <div className="lg:flex">
                      {/* Step Number & Icon */}
                      <div className="lg:w-1/4 bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 text-center">
                        <div className="text-6xl mb-4" aria-hidden="true">{step?.icon}</div>
                        <div className="w-16 h-16 bg-white text-blue-600 rounded-full flex items-center justify-center font-black text-2xl mx-auto mb-4">
                          {step?.step}
                        </div>
                        <h3 className="text-xl font-bold mb-2">{step?.title}</h3>
                        <div className="bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">
                          <Clock className="w-4 h-4 inline mr-1" aria-hidden="true" />
                          {step?.duration}
                        </div>
                      </div>

                      {/* Step Details */}
                      <div className="lg:w-3/4 p-8">
                        <div className="mb-6">
                          <h4 className="text-2xl font-bold text-gray-900 mb-4">
                            {language === 'es' ? 'Qué Sucede' : 'What Happens'}
                          </h4>
                          <p className="text-lg text-gray-700 leading-relaxed mb-6">
                            {step?.description}
                          </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Requirements */}
                          <div>
                            <h5 className="font-bold text-gray-900 mb-3 flex items-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-2" aria-hidden="true" />
                              {language === 'es' ? 'Requisitos' : 'Requirements'}
                            </h5>
                            <ul className="space-y-2">
                              {step?.requirements?.map((req, reqIndex) => (
                                <li key={reqIndex} className="text-gray-600 flex items-start">
                                  <span className="font-bold text-blue-600 mr-2 mt-1">•</span>
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Timeframe Info */}
                          <div>
                            <h5 className="font-bold text-gray-900 mb-3 flex items-center">
                              <AlertCircle className="w-5 h-5 text-amber-600 mr-2" aria-hidden="true" />
                              {language === 'es' ? 'Tiempo Importante' : 'Timing Note'}
                            </h5>
                            <p className="text-gray-600 bg-amber-50 p-4 rounded-lg border border-amber-200">
                              {step?.timeframe}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Emergency Contact Strip */}
            <div className="mt-16 bg-red-600 text-white p-6 rounded-2xl text-center">
              <h3 className="text-2xl font-bold mb-4">
                {language === 'es' ? 'Proceso Confuso? ¡Nosotros Lo Manejamos!' : 'Process Confusing? We Handle Everything!'}
              </h3>
              <p className="text-xl mb-6">
                {language === 'es' ?'No necesita entender cada paso - solo necesita nuestro número de teléfono.' :'You don\'t need to understand every step - you just need our phone number.'
                }
              </p>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-all inline-flex items-center"
              >
                <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                {formatPhoneDisplay(fallbackPhone)} - {language === 'es' ? 'Disponible Ahora' : 'Available Now'}
              </a>
            </div>
          </div>
        </section>

        {/* Interactive Bail Calculator */}
        <section id="bail-calculator" className="py-20 bg-white">
          <BailEstimator language={language} />
        </section>

        {/* County-Specific Process Variations */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <CountyProcessGuide language={language} />
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white" role="region" aria-labelledby="faq-heading">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="faq-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                {language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                {language === 'es' ?'Respuestas honestas a las preguntas que cada familia tiene sobre fianzas.' :'Honest answers to the questions every family has about bail bonds.'
                }
              </p>
            </div>

            <div className="space-y-8">
              {currentFAQ?.map((category, categoryIndex) => (
                <div key={categoryIndex} className="bg-gray-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl">
                    {category?.category}
                  </h3>
                  
                  <div className="space-y-4">
                    {category?.questions?.map((faq, faqIndex) => {
                      const globalIndex = categoryIndex * 100 + faqIndex;
                      const isExpanded = expandedFAQ === globalIndex;
                      
                      return (
                        <div key={globalIndex} className="bg-white rounded-xl shadow-lg border border-gray-200">
                          <button
                            onClick={() => toggleFAQ(globalIndex)}
                            className="w-full p-6 text-left hover:bg-gray-50 transition-colors rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            aria-expanded={isExpanded}
                          >
                            <div className="flex justify-between items-start">
                              <h4 className="text-lg font-bold text-gray-900 pr-4 leading-tight">
                                {faq?.question}
                              </h4>
                              {isExpanded ? (
                                <ChevronUp className="w-6 h-6 text-blue-600 flex-shrink-0" aria-hidden="true" />
                              ) : (
                                <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" aria-hidden="true" />
                              )}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="px-6 pb-6">
                              <div className="border-t border-gray-200 pt-4">
                                <p className="text-gray-700 text-lg leading-relaxed">
                                  {faq?.answer}
                                </p>
                                
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <a 
                                    href={`tel:${fallbackPhone}`}
                                    className="text-blue-600 hover:text-blue-800 font-semibold flex items-center group"
                                  >
                                    {language === 'es' ? 'Llámenos sobre esta pregunta' : 'Call us about this question'}
                                    <Phone className="w-4 h-4 ml-1 group-hover:animate-pulse" aria-hidden="true" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Still Have Questions CTA */}
            <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-2xl text-center">
              <h3 className="text-2xl font-bold mb-4">
                {language === 'es' ? '¿Todavía Tiene Preguntas?' : 'Still Have Questions?'}
              </h3>
              <p className="text-xl mb-6">
                {language === 'es' ?'Hable con un agente licenciado que le explicará todo en términos simples.' :'Speak with a licensed agent who will explain everything in simple terms.'
                }
              </p>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center"
              >
                <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                {language === 'es' ? 'Llamar Agente Ahora' : 'Call Agent Now'}: {formatPhoneDisplay(fallbackPhone)}
              </a>
            </div>
          </div>
        </section>

        {/* Downloadable Checklists */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <DownloadableChecklists language={language} />
        </section>

        {/* Trust Building & Credentials */}
        <section className="py-20 bg-white">
          <CredentialsShowcase language={language} />
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <div className="mb-8">
              <AlertCircle className="w-16 h-16 mx-auto mb-6 animate-pulse" aria-hidden="true" />
              <h2 className="text-4xl lg:text-5xl font-black mb-6">
                {language === 'es' ? 'Proceso Aclarado. ¿Listo Para Comenzar?' : 'Process Clear. Ready to Start?'}
              </h2>
              <p className="text-2xl mb-4 leading-tight">
                {language === 'es' ?'Ahora sabe cómo funciona - permítanos manejar los detalles' :'Now you know how it works - let us handle the details'
                }
              </p>
              <p className="text-xl text-red-100 mb-12">
                {language === 'es' ?'Agentes licenciados disponibles ahora para comenzar el proceso inmediatamente' :'Licensed agents standing by to start the process immediately'
                }
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-8 lg:px-12 py-5 lg:py-6 rounded-2xl font-black text-xl lg:text-2xl hover:bg-red-50 transition-all transform hover:scale-105 shadow-2xl flex items-center"
              >
                <Phone className="w-8 h-8 mr-4" aria-hidden="true" />
                {language === 'es' ? 'Comenzar Proceso' : 'Start Process'}: {formatPhoneDisplay(fallbackPhone)}
              </a>
              
              <div className="text-red-100 font-bold">
                {language === 'es' ? 'Respuesta en menos de 10 segundos' : 'Answer in under 10 seconds'}
              </div>
            </div>

            <div className="mt-12 bg-white/20 rounded-2xl p-8 backdrop-blur-sm">
              <div className="grid md:grid-cols-2 gap-6 text-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>{language === 'es' ? 'Disponible 24/7/365' : 'Available 24/7/365'}</strong> - {language === 'es' ? 'Nunca cerrado' : 'Never closed'}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>{language === 'es' ? 'Sin Obligación' : 'No Obligation'}</strong> - {language === 'es' ? 'Si no procede, no paga' : 'If you don\'t proceed, you pay nothing'}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>{language === 'es' ? 'Licenciados y Asegurados' : 'Licensed & Insured'}</strong> - {language === 'es' ? 'Servicio profesional completo' : 'Full professional service'}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>{language === 'es' ? 'Planes de Pago' : 'Payment Plans'}</strong> - {language === 'es' ? 'Trabajamos con su presupuesto' : 'We work with your budget'}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BailGuideWithFAQ;